import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "../lib/api";
import { AppLayout } from "../components/tabon/AppLayout";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Copy, Trash2, Plus, Pencil, CheckCircle2, MessageSquare } from "lucide-react";
import ServicePointsManager from "../components/tabon/ServicePoints";

// Build identity shown in the footer. The hashed main bundle name changes every production
// build, so it tells QA at a glance whether a stale bundle is being served (a stale UI cost an
// hour chasing a phantom bug). In the dev server the bundle is unhashed, so this reads "dev".
const BUILD_ID = (() => {
  try {
    const src = [...document.querySelectorAll("script[src]")]
      .map((e) => e.src)
      .find((s) => /\/static\/js\/main\.[a-f0-9]+\.js/.test(s));
    const m = src && src.match(/main\.([a-f0-9]+)\.js/);
    return m ? m[1].slice(0, 8) : "dev";
  } catch { return "dev"; }
})();

// The settings sections (left rail on desktop, top tabs on mobile). `feature` hides a section
// unless the venue has that feature on — Ordering/Billing are invisible on a venue without
// ordering (no production venue has it yet).
const SECTIONS = [
  { key: "venue", label: "Venue" },
  { key: "staff", label: "Staff & Access" },
  { key: "ordering", label: "Ordering", feature: "ordering" },
  { key: "billing", label: "Billing & Tax", feature: "ordering" },
  { key: "loyalty", label: "Loyalty" },
  { key: "bottle_locker", label: "Bottle Locker" },
  { key: "menu", label: "Menu & Branding" },
  { key: "notifications", label: "Notifications" },
  { key: "integrations", label: "Integrations" },
];

// The computed-bill order, display only — no bill computes any of this yet (config only). Kept
// in ONE place so changing the order is a one-line change. Discount-before-tax and
// GST-on-the-discounted-amount are verified against a real Petpooja tax invoice; the
// The CCPA service-charge guidance (verbatim from the backend's SERVICE_CHARGE_CCPA_HELP); shown
// beside the "Add automatically" toggle it explains (AA-3).
const CCPA_SERVICE_CHARGE_HELP = "CCPA guidelines (4 July 2022, upheld by the Delhi High Court on 28 March 2025) require service charge to be voluntary. Leaving this off adds it only when your staff choose to.";

// The computed-bill explainer, now describing the venue's REAL charges (AA-3) rather than a single
// legacy service-charge percent. Lists the active charge labels and which feed the tax base.
const billOrderRows = (charges) => {
  const active = (charges || []).filter(c => c.active !== false);
  const taxed = active.filter(c => c.taxable).map(c => c.label || "charge");
  const names = active.map(c => c.label || "charge");
  return [
    { sign: "", label: "Subtotal" },
    { sign: "−", label: "Discount, comps, points redeemed" },
    { sign: "=", label: "Net", note: "loyalty earns on this" },
    { sign: "+", label: "GST", note: taxed.length ? `on net + ${taxed.join(", ")}` : "on net" },
    { sign: "+", label: names.length ? `Charges: ${names.join(", ")}` : "Charges (none defined)", note: "per order type, opt-in" },
    { sign: "±", label: "Round off" },
    { sign: "=", label: "Payable", bold: true },
  ];
};

export default function Settings() {
  const { user, refresh, hasFeature } = useAuth();
  // null until populated from user.venue in the effect below (then the loading gate falls
  // through). A settings form must never hold a value it did not load — so there are no
  // hardcoded field defaults here, and every field (including the loyalty / redemption /
  // payment config that used to be fetched separately) is read synchronously from user.venue,
  // which AuthContext has already loaded. That removes the second fetch, the seeded defaults and
  // the race that let a Save overwrite real config with defaults.
  const [form, setForm] = useState(null);
  const [signupBonus, setSignupBonus] = useState(0);
  // SA-provisioned payment flags — read-only here, only used to gate the Payments card
  const [paymentCfg, setPaymentCfg] = useState({ enabled: false, provider: null });
  // Event campaign frequency cap (0 = off)
  const [eventCap, setEventCap] = useState(0);
  const [eventCapSaving, setEventCapSaving] = useState(false);
  // Birthday campaign settings
  const [birthdayEnabled, setBirthdayEnabled] = useState(false);
  const [birthdayMultiplier, setBirthdayMultiplier] = useState(2);
  const [birthdayWindow, setBirthdayWindow] = useState(3);
  const [anniversaryEnabled, setAnniversaryEnabled] = useState(false);   // AA-8 — same machine, second date
  const [anniversaryMultiplier, setAnniversaryMultiplier] = useState(2);
  const [anniversaryWindow, setAnniversaryWindow] = useState(3);
  // Visit streak settings
  const [streakEnabled, setStreakEnabled] = useState(false);
  const [streakBonus, setStreakBonus] = useState(50);
  const [streakBonusType, setStreakBonusType] = useState("fixed"); // "fixed" | "multiplier"
  const [streakBonusMultiplier, setStreakBonusMultiplier] = useState(2);
  const [streakCount, setStreakCount] = useState(3);
  const [streakDays, setStreakDays] = useState(14);
  const [overridePin, setOverridePin] = useState("");
  const [overridePinSaving, setOverridePinSaving] = useState(false);
  // Ordering & POS settings
  const [orderingMode, setOrderingMode] = useState("takeaway");
  const [orderingTableCount, setOrderingTableCount] = useState(10);
  const [orderingOpen, setOrderingOpen] = useState(true);
  const [receiptType, setReceiptType] = useState("simple");
  const [invoicePrefix, setInvoicePrefix] = useState("INV");
  const [venueAddress, setVenueAddress] = useState("");
  const [venuePhone, setVenuePhone] = useState("");
  const [gstin, setGstin] = useState("");
  // Z-3: tax_table is authoritative. Each row {group, label, rate, split}; food uses statutory slabs,
  // other groups 0–30. gst_rate is no longer written from here — it survives only as the migration
  // input and compute_bill's derived flat field.
  const [taxTable, setTaxTable] = useState([]);
  const [orderingSettingsSaving, setOrderingSettingsSaving] = useState(false);
  const [eodReportEnabled, setEodReportEnabled] = useState(false);
  const [eodReportTime, setEodReportTime] = useState("23:00");
  const [roomServiceEnabled, setRoomServiceEnabled] = useState(false);
  const [roomServiceStart, setRoomServiceStart] = useState("07:00");
  const [roomServiceEnd, setRoomServiceEnd] = useState("23:00");
  // Reports & day close
  const [businessDayStart, setBusinessDayStart] = useState("00:00");
  const [dayCloseEnabled, setDayCloseEnabled] = useState(false);
  const [openingFloatDefault, setOpeningFloatDefault] = useState(0);
  // Ordering (new config fields — Part 5)
  const [autoKotEnabled, setAutoKotEnabled] = useState(false);
  const [defaultCovers, setDefaultCovers] = useState(2);
  const [requireSteward, setRequireSteward] = useState(false);
  const [takeawayVehicleEnabled, setTakeawayVehicleEnabled] = useState(false);
  // Billing & Tax (new config fields — Part 6)
  const [fssaiNumber, setFssaiNumber] = useState("");
  const [charges, setCharges] = useState([]);                                   // the venue's full charge_definitions family (AA-3)
  const [discountPinThreshold, setDiscountPinThreshold] = useState(10);         // discount % above which the override PIN is required
  const [receiptWidthMm, setReceiptWidthMm] = useState(80);                     // thermal roll width 58|80 (AA-5)
  const [discountMaxTotal, setDiscountMaxTotal] = useState(100);                // cumulative discount ceiling %, refused even with the PIN (AA-5)
  const [dineInQrAttaches, setDineInQrAttaches] = useState(false);              // QR dine-in order joins the table's running tab (AA-5)
  const [billingSaving, setBillingSaving] = useState(false);
  // Section nav + per-section dirty state (Ordering & Billing each own one explicit Save; nothing
  // auto-saves on blur, and a dirty section warns before navigating away).
  const [section, setSection] = useState("venue");
  const [orderingDirty, setOrderingDirty] = useState(false);
  const [billingDirty, setBillingDirty] = useState(false);
  // Print log — read-only support view in the Ordering section (AH-3).
  const [printLog, setPrintLog] = useState(null);
  const [printVerdict, setPrintVerdict] = useState(null);
  const [printLogLoading, setPrintLogLoading] = useState(false);
  const loadPrintLog = useCallback(async () => {
    setPrintLogLoading(true);
    try {
      const { data } = await api.get("/print-log");
      setPrintLog(data.rows || []);
      setPrintVerdict(data.verdict || null);
    } catch (_) { /* a read-only support view stays quiet on failure */ }
    finally { setPrintLogLoading(false); }
  }, []);
  // Load it when the Ordering section opens — that section only renders for ordering venues, so
  // gating on `section` alone is enough (loadPrintLog is stable, so this won't refetch on every render).
  useEffect(() => { if (section === "ordering") loadPrintLog(); }, [section, loadPrintLog]);

  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", pin: "" });
  const [milestones, setMilestones] = useState([]);
  const [msForm, setMsForm] = useState({ milestone_points: 100, reward_description: "", reward_type: "free_item" });
  // Stamp programs
  const [stampPrograms, setStampPrograms] = useState([]);
  const [spForm, setSpForm] = useState({ name: "", item_label: "", required_stamps: 10, reward_title: "" });
  const [spEditId, setSpEditId] = useState(null);
  const [spEditForm, setSpEditForm] = useState({});
  // WhatsApp templates (explainer only — template HEALTH is TabOn's concern, shown in superadmin, J-2b)
  const [templates, setTemplates] = useState([]);
  // Menu slug (for T&Cs preview link)
  const [menuSlug, setMenuSlug] = useState("");

  useEffect(() => {
    if (user?.venue) {
      setForm({
        name: user.venue.name,
        default_expiry_days: user.venue.default_expiry_days,
        default_peg_sizes: user.venue.default_peg_sizes,
        feedback_form_url: user.venue.feedback_form_url || "",
        timezone: user.venue.timezone || "Asia/Kolkata",
        loyalty_rate_per_100: user.venue.loyalty_rate_per_100 ?? 10,
        loyalty_feedback_bonus: user.venue.loyalty_feedback_bonus ?? 5,
        menu_pure_veg: user.venue.menu_pure_veg ?? false,
        send_welcome_message: user.venue.send_welcome_message ?? false,
        allow_points_split: user.venue.allow_points_split ?? false,
        bottle_menu_promo: user.venue.bottle_menu_promo ?? "none",
        // Previously seeded with defaults here then overwritten by a second /menu/settings fetch
        // (the race). Now read straight from the venue payload, which serves them identically.
        loyalty_terms: Array.isArray(user.venue.loyalty_terms) ? user.venue.loyalty_terms : [],
        bottle_locker_terms: user.venue.bottle_locker_terms || "",
        redemption_max_percent: user.venue.redemption_max_percent ?? 100,
        redemption_min_bill: user.venue.redemption_min_bill ?? 0,
        redemption_cooldown: user.venue.redemption_cooldown || "off",
        payment_methods_takeaway: (Array.isArray(user.venue.payment_methods_takeaway) && user.venue.payment_methods_takeaway.length
          ? user.venue.payment_methods_takeaway : ["cash"]),
      });
      // menu_slug and the SA-provisioned payment flags now ride the venue payload too, so the
      // separate /menu/settings fetch is gone entirely.
      setMenuSlug(user.venue.menu_slug || "");
      setPaymentCfg({ enabled: user.venue.payments_enabled === true, provider: user.venue.payment_provider || null });
      setSignupBonus(user.venue.signup_bonus_points ?? 0);
      setEventCap(user.venue.event_campaign_frequency_cap ?? 0);
      // Birthday campaign
      setBirthdayEnabled(user.venue.birthday_campaign_enabled ?? false);
      setBirthdayMultiplier(user.venue.birthday_points_multiplier ?? 2);
      setBirthdayWindow(user.venue.birthday_offer_window_days ?? 3);
      setAnniversaryEnabled(user.venue.anniversary_campaign_enabled ?? false);
      setAnniversaryMultiplier(user.venue.anniversary_points_multiplier ?? 2);
      setAnniversaryWindow(user.venue.anniversary_offer_window_days ?? 3);
      // Visit streak
      setStreakEnabled(user.venue.streak_enabled ?? false);
      setStreakBonus(user.venue.streak_bonus_points ?? 50);
      setStreakBonusType(user.venue.streak_bonus_type ?? "fixed");
      setStreakBonusMultiplier(user.venue.streak_bonus_multiplier ?? 2);
      setStreakCount(user.venue.streak_visit_count ?? 3);
      setStreakDays(user.venue.streak_window_days ?? 14);
      // Ordering & POS settings
      setOrderingMode(user.venue.ordering_mode ?? "takeaway");
      setOrderingTableCount(user.venue.ordering_table_count ?? 10);
      setOrderingOpen(user.venue.ordering_open ?? true);
      setReceiptType(user.venue.receipt_type ?? "simple");
      setInvoicePrefix(user.venue.invoice_prefix ?? "INV");
      setVenueAddress(user.venue.venue_address ?? "");
      setVenuePhone(user.venue.venue_phone ?? "");
      setGstin(user.venue.gstin ?? "");
      // Prefer the venue's tax_table; fall back to one food group derived from the legacy gst_rate so
      // a venue the migration hasn't reached still shows its current rate (and _group_rate still bills).
      const _tt = (Array.isArray(user.venue.tax_table) && user.venue.tax_table.length)
        ? user.venue.tax_table
        : [{ group: "food", label: "Food & non-alcoholic", rate: user.venue.gst_rate ?? 0, split: "cgst_sgst" }];
      setTaxTable(_tt.map(t => ({ group: t.group || "food", label: t.label || "", rate: Number(t.rate) || 0, split: t.split === "single" ? "single" : "cgst_sgst" })));
      setEodReportEnabled(user.venue.eod_report_enabled ?? false);
      setEodReportTime(user.venue.eod_report_time ?? "23:00");
      setRoomServiceEnabled(user.venue.room_service_enabled ?? false);
      setRoomServiceStart(user.venue.room_service_start ?? "07:00");
      setRoomServiceEnd(user.venue.room_service_end ?? "23:00");
      setBusinessDayStart(user.venue.business_day_start ?? "00:00");
      setDayCloseEnabled(user.venue.day_close_enabled ?? false);
      setOpeningFloatDefault(user.venue.opening_float_default ?? 0);
      // Ordering + Billing new config fields
      setAutoKotEnabled(user.venue.auto_kot_enabled ?? false);
      setTakeawayVehicleEnabled(user.venue.takeaway_vehicle_enabled ?? false);
      setDefaultCovers(user.venue.default_covers ?? 2);
      setRequireSteward(user.venue.require_steward ?? false);
      setFssaiNumber(user.venue.fssai_number ?? "");
      setReceiptWidthMm(user.venue.receipt_width_mm === 58 ? 58 : 80);
      setDiscountMaxTotal(user.venue.discount_max_total_percent ?? 100);
      setDineInQrAttaches(user.venue.dine_in_qr_attaches_to_check ?? false);
      // The whole charge family (AA-3), edited row-by-row in the charges section below. serialize_user
      // ships the DERIVED charge_definitions (the legacy service charge included before migration), so
      // this is populated for every venue. No magic-id lookup — each charge is edited as itself.
      setCharges((Array.isArray(user.venue.charge_definitions) ? user.venue.charge_definitions : []).map(c => ({
        id: c.id || "", label: c.label || "", mode: c.mode === "fixed" ? "fixed" : "percent",
        value: c.value ?? 0, base: c.base || "core",
        applies_to: Array.isArray(c.applies_to) && c.applies_to.length ? c.applies_to : ["dine_in"],
        auto_apply: !!c.auto_apply, taxable: !!c.taxable, active: c.active !== false,
      })));
      // Discount PIN threshold: the % above which a dine-in discount needs the override PIN.
      // Carry over a previously-entered discount_max_percent so nothing is lost (see DEVIATIONS).
      setDiscountPinThreshold(user.venue.discount_pin_threshold_percent ?? user.venue.discount_max_percent ?? 10);
      // Freshly loaded (or reloaded after a save) → the two guarded sections are clean.
      setOrderingDirty(false);
      setBillingDirty(false);
    }
    loadStaff();
    loadMilestones();
    loadStampPrograms();
    loadTemplates();
  }, [user?.venue]);

  // Warn on a full page unload (reload / close / navigate away) while a guarded section has
  // unsaved changes — the in-app section switch is guarded separately in switchSection.
  useEffect(() => {
    if (!orderingDirty && !billingDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [orderingDirty, billingDirty]);

  const loadMilestones = async () => {
    try { const { data } = await api.get("/milestones"); setMilestones(data); } catch {}
  };

  const loadStampPrograms = async () => {
    try { const { data } = await api.get("/stamp-programs"); setStampPrograms(data); } catch {}
  };
  const addStampProgram = async () => {
    if (!spForm.name.trim() || !spForm.item_label.trim() || !spForm.reward_title.trim()) {
      toast.error("Name, item label, and reward title are required"); return;
    }
    try {
      await api.post("/stamp-programs", {
        name: spForm.name.trim(),
        item_label: spForm.item_label.trim(),
        required_stamps: Number(spForm.required_stamps) || 10,
        reward_title: spForm.reward_title.trim(),
      });
      setSpForm({ name: "", item_label: "", required_stamps: 10, reward_title: "" });
      await loadStampPrograms();
      toast.success("Stamp program created");
    } catch (err) { toast.error(formatError(err)); }
  };
  const saveStampProgram = async (id) => {
    try {
      await api.put(`/stamp-programs/${id}`, spEditForm);
      setSpEditId(null);
      await loadStampPrograms();
      toast.success("Saved");
    } catch (err) { toast.error(formatError(err)); }
  };
  const toggleStampProgram = async (id, active) => {
    try {
      await api.put(`/stamp-programs/${id}`, { active });
      await loadStampPrograms();
    } catch (err) { toast.error(formatError(err)); }
  };
  const deleteStampProgram = async (id) => {
    if (!window.confirm("Delete this stamp program? All customer progress will be lost.")) return;
    try { await api.delete(`/stamp-programs/${id}`); await loadStampPrograms(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const loadTemplates = async () => {
    try { const { data } = await api.get("/templates/info"); setTemplates(data); } catch {}
  };
  const addMilestone = async () => {
    if (!msForm.reward_description.trim()) { toast.error("Enter reward description"); return; }
    try {
      await api.post("/milestones", {
        milestone_points: Number(msForm.milestone_points),
        reward_description: msForm.reward_description.trim(),
        reward_type: msForm.reward_type,
      });
      setMsForm({ milestone_points: 100, reward_description: "", reward_type: "free_item" });
      await loadMilestones();
      toast.success("Milestone added");
    } catch (err) { toast.error(formatError(err)); }
  };
  const toggleMilestone = async (id, active) => {
    try {
      await api.put(`/milestones/${id}`, { is_active: active });
      await loadMilestones();
    } catch (err) { toast.error(formatError(err)); }
  };
  const removeMilestone = async (id) => {
    if (!window.confirm("Delete this milestone?")) return;
    try { await api.delete(`/milestones/${id}`); await loadMilestones(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const loadStaff = async () => {
    try {
      const { data } = await api.get("/staff");
      setStaff(data);
    } catch {}
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // One request, one endpoint. Every field here lives on the same venues document and is
      // owned by PUT /settings — including the loyalty / redemption / payment config that used to
      // be split off to /menu/settings (the data-loss bug). menu_pure_veg is the only menu-scoped
      // field on this screen; it auto-saves on its own toggle, so it is excluded here. No
      // `|| default` coercion on the load-bearing values — send them as entered and let the
      // backend validate and reject, rather than silently substituting a default.
      const { menu_pure_veg, ...rest } = form;
      await api.put("/settings", {
        ...rest,
        redemption_max_percent: Number(form.redemption_max_percent),
        redemption_min_bill: Number(form.redemption_min_bill),
        loyalty_terms: (Array.isArray(form.loyalty_terms) ? form.loyalty_terms : [])
          .map((s) => String(s).trim()).filter(Boolean),
        bottle_locker_terms: (form.bottle_locker_terms || "").trim(),
        payment_methods_takeaway: (Array.isArray(form.payment_methods_takeaway) && form.payment_methods_takeaway.length
          ? form.payment_methods_takeaway : ["cash"]),
      });
      toast.success("Settings saved");
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  // Auto-save terms / redemption / payment fields on blur. These moved to PUT /settings (Part 2);
  // they live on the same venues document as everything else this screen writes.
  const updateTerms = async (patch) => {
    try {
      await api.put("/settings", patch);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const saveBirthdaySettings = async () => {
    const multiplier = Number(birthdayMultiplier);
    const window = Number(birthdayWindow);
    if (multiplier < 2 || multiplier > 5) { toast.error("Multiplier must be 2–5"); return; }
    if (window < 1 || window > 7) { toast.error("Offer window must be 1–7 days"); return; }
    try {
      await api.put("/settings", {
        birthday_campaign_enabled: birthdayEnabled,
        birthday_points_multiplier: multiplier,
        birthday_offer_window_days: window,
      });
      toast.success("Birthday campaign saved");
      await refresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  const saveAnniversarySettings = async () => {
    const multiplier = Number(anniversaryMultiplier);
    const window = Number(anniversaryWindow);
    if (multiplier < 2 || multiplier > 5) { toast.error("Multiplier must be 2–5"); return; }
    if (window < 1 || window > 7) { toast.error("Offer window must be 1–7 days"); return; }
    try {
      await api.put("/settings", {
        anniversary_campaign_enabled: anniversaryEnabled,
        anniversary_points_multiplier: multiplier,
        anniversary_offer_window_days: window,
      });
      toast.success("Anniversary campaign saved");
      await refresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  const saveStreakSettings = async () => {
    const count = Number(streakCount);
    const days = Number(streakDays);
    const bonus = Number(streakBonus);
    const multiplier = Number(streakBonusMultiplier);
    if (count < 2 || count > 20) { toast.error("Visit count must be 2–20"); return; }
    if (days < 1 || days > 90) { toast.error("Window must be 1–90 days"); return; }
    if (streakBonusType === "fixed" && (bonus < 1 || bonus > 500)) { toast.error("Bonus must be 1–500 pts"); return; }
    if (streakBonusType === "multiplier" && (multiplier < 1.5 || multiplier > 5)) { toast.error("Multiplier must be 1.5–5x"); return; }
    try {
      await api.put("/settings", {
        streak_enabled: streakEnabled,
        streak_visit_count: count,
        streak_window_days: days,
        streak_bonus_points: streakBonusType === "fixed" ? bonus : null,
        streak_bonus_type: streakBonusType,
        streak_bonus_multiplier: streakBonusType === "multiplier" ? multiplier : null,
      });
      toast.success("Streak settings saved");
      await refresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  // Ordering section — one explicit Save (no auto-save on blur). Clears the dirty flag only on
  // success so a failed save still warns before navigating away.
  const saveOrdering = async () => {
    const covers = Number(defaultCovers);
    if (!(covers >= 1 && covers <= 20)) { toast.error("Default covers must be between 1 and 20"); return; }
    setOrderingSettingsSaving(true);
    try {
      await api.put("/settings", {
        ordering_open: orderingOpen,
        ordering_mode: orderingMode,
        ordering_table_count: Number(orderingTableCount),
        room_service_enabled: roomServiceEnabled,
        room_service_start: roomServiceStart,
        room_service_end: roomServiceEnd,
        auto_kot_enabled: autoKotEnabled,
        takeaway_vehicle_enabled: takeawayVehicleEnabled,
        default_covers: covers,
        require_steward: requireSteward,
        dine_in_qr_attaches_to_check: dineInQrAttaches,   // QR dine-in joins the table check (AA-5)
      });
      toast.success("Ordering settings saved");
      setOrderingDirty(false);
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setOrderingSettingsSaving(false);
    }
  };

  // Tax groups editor (Z-3) — row updates mark the section dirty like every other billing control.
  // No addTaxGroup: creating a second group is disabled until the invoice can report per-rate (AA-2).
  // removeTaxGroup stays so a venue that already has a second group can drop back to one.
  const updateTaxRow = (i, patch) => { setTaxTable(tt => tt.map((r, j) => j === i ? { ...r, ...patch } : r)); setBillingDirty(true); };
  const removeTaxGroup = (i) => { setTaxTable(tt => tt.filter((_, j) => j !== i)); setBillingDirty(true); };

  // Charges editor (AA-3) — label / percent-or-fixed / value / order types / taxable / auto-apply / active.
  const updateCharge = (i, patch) => { setCharges(cs => cs.map((c, j) => j === i ? { ...c, ...patch } : c)); setBillingDirty(true); };
  const addCharge = () => { setCharges(cs => [...cs, { id: "", label: "", mode: "percent", value: 0, base: "core", applies_to: ["dine_in"], auto_apply: false, taxable: false, active: true }]); setBillingDirty(true); };
  const removeCharge = (i) => { setCharges(cs => cs.filter((_, j) => j !== i)); setBillingDirty(true); };
  const toggleChargeOrderType = (i, ot) => updateCharge(i, {
    applies_to: charges[i].applies_to.includes(ot) ? charges[i].applies_to.filter(x => x !== ot) : [...charges[i].applies_to, ot],
  });

  // Billing & Tax section — one explicit Save. Tax groups / discount limits are not low-stakes, so
  // nothing here auto-saves.
  const saveBilling = async () => {
    // Charges (AA-3): backend re-validates; check here for a clear message. Each needs a label, a
    // mode of percent|fixed, a value in range (percent 0–100, fixed >= 0), and one+ order type.
    for (const c of charges) {
      if (!(c.label || "").trim()) { toast.error("Every charge needs a label"); return; }
      const v = Number(c.value);
      if (!(v >= 0) || (c.mode === "percent" && v > 100)) { toast.error(`"${c.label || "charge"}" has an invalid value`); return; }
      if (!Array.isArray(c.applies_to) || c.applies_to.length === 0) { toast.error(`"${c.label || "charge"}" must apply to at least one order type`); return; }
    }
    // Tax groups (Z-3): the backend re-validates, but check here for a clear message. Food must carry
    // a statutory slab; other groups 0–30; keys unique; a 'food' group must exist (items default to it).
    const _seen = new Set();
    for (const t of taxTable) {
      const g = (t.group || "").trim();
      if (!g) { toast.error("Every tax group needs a key"); return; }
      if (_seen.has(g)) { toast.error(`Duplicate tax group "${g}"`); return; }
      _seen.add(g);
      const rate = Number(t.rate);
      if (g === "food") {
        if (![0, 5, 12, 18].includes(rate)) { toast.error("Food GST must be 0, 5, 12 or 18%"); return; }
      } else if (!(rate >= 0 && rate <= 30)) { toast.error(`Rate for "${g}" must be between 0 and 30%`); return; }
    }
    if (!_seen.has("food")) { toast.error("A 'food' tax group is required"); return; }
    const dthr = Number(discountPinThreshold);
    if (!(Number.isInteger(dthr) && dthr >= 0 && dthr <= 100)) { toast.error("Discount PIN threshold must be 0–100%"); return; }
    const dmt = Number(discountMaxTotal);
    if (!(Number.isInteger(dmt) && dmt >= 0 && dmt <= 100)) { toast.error("Max total discount must be 0–100%"); return; }
    const fssai = fssaiNumber.trim();
    if (fssai && !/^\d{14}$/.test(fssai)) { toast.error("FSSAI number must be exactly 14 digits"); return; }
    // Clean the whole charge family for the save: trimmed labels, numeric values, a stable id (empty
    // → the backend assigns a uuid). No special-casing of a service charge — every row is itself.
    const chargeDefs = charges.map(c => ({
      id: (c.id || "").trim(),
      label: (c.label || "").trim(),
      mode: c.mode === "fixed" ? "fixed" : "percent",
      value: Number(c.value) || 0,
      base: c.base || "core",
      applies_to: c.applies_to,
      auto_apply: !!c.auto_apply,
      taxable: !!c.taxable,
      active: c.active !== false,
    }));
    setBillingSaving(true);
    try {
      await api.put("/settings", {
        receipt_type: receiptType,
        gstin: gstin.trim() || null,
        receipt_width_mm: receiptWidthMm,                       // thermal roll width (AA-5)
        discount_max_total_percent: Number(discountMaxTotal),   // cumulative discount ceiling (AA-5)
        // tax_table is now the single source of truth for GST (Z-3); gst_rate is no longer written.
        tax_table: taxTable.map(t => ({ group: (t.group || "").trim(), label: (t.label || "").trim() || (t.group || "").trim(), rate: Number(t.rate), split: t.split })),
        invoice_prefix: invoicePrefix.trim().toUpperCase() || "INV",
        venue_address: venueAddress.trim() || null,
        venue_phone: venuePhone.trim() || null,
        business_day_start: businessDayStart,
        day_close_enabled: dayCloseEnabled,
        opening_float_default: Number(openingFloatDefault) || 0,
        eod_report_enabled: eodReportEnabled,
        eod_report_time: eodReportTime,
        fssai_number: fssai || null,
        // Charges family is now canonical; legacy service_charge_* are no longer written.
        charge_definitions: chargeDefs,
        // One knob for the PIN threshold; mirror into the legacy discount_max_percent so a reader
        // of the old field stays in sync for the one-release deprecation window.
        discount_pin_threshold_percent: dthr,
        discount_max_percent: dthr,
      });
      toast.success("Billing settings saved");
      setBillingDirty(false);
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBillingSaving(false);
    }
  };

  // Switch section, warning if the section being left has unsaved changes.
  const switchSection = (next) => {
    if (next === section) return;
    const leavingDirty = (section === "ordering" && orderingDirty) || (section === "billing" && billingDirty);
    if (leavingDirty && !window.confirm("You have unsaved changes in this section. Leave without saving?")) return;
    setSection(next);
  };

  const saveSignupBonus = async () => {
    const pts = Number(signupBonus);
    if (pts < 0 || pts > 10000) { toast.error("Bonus must be 0–10000 pts"); return; }
    try {
      await api.put("/settings", { signup_bonus_points: pts });
      toast.success("Signup bonus saved");
      await refresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  const saveEventCap = async () => {
    const cap = Number(eventCap);
    if (!Number.isInteger(cap) || cap < 0 || cap > 20) {
      toast.error("Cap must be a whole number between 0 and 20"); return;
    }
    setEventCapSaving(true);
    try {
      await api.put("/settings", { event_campaign_frequency_cap: cap });
      toast.success(cap === 0 ? "Frequency cap turned off" : "Frequency cap saved");
      await refresh();
    } catch (err) { toast.error(formatError(err)); }
    finally { setEventCapSaving(false); }
  };

  const togglePeg = (p) => {
    const cur = form.default_peg_sizes.includes(p)
      ? form.default_peg_sizes.filter((x) => x !== p)
      : [...form.default_peg_sizes, p];
    setForm({ ...form, default_peg_sizes: cur.length ? cur : [p] });
  };

  const addStaff = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff", newStaff);
      toast.success("Staff added");
      setNewStaff({ name: "", pin: "" });
      loadStaff();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const delStaff = async (id) => {
    await api.delete(`/staff/${id}`);
    toast.success("Removed");
    loadStaff();
  };

  const copyCode = () => {
    navigator.clipboard.writeText(user?.venue?.venue_code || "");
    toast.success("Venue code copied");
  };

  const saveOverridePin = async () => {
    if (overridePin !== "" && (!/^\d{4}$/.test(overridePin))) {
      toast.error("Override PIN must be exactly 4 digits");
      return;
    }
    setOverridePinSaving(true);
    try {
      await api.put("/settings", { override_pin: overridePin });
      toast.success(overridePin === "" ? "Override PIN cleared" : "Override PIN saved");
      setOverridePin("");
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setOverridePinSaving(false);
    }
  };

  // Loading gate: the form is populated synchronously from user.venue in the effect above, so it
  // is ready before the admin can interact. Until then we render nothing editable — the form must
  // never show (or save) a value it did not load.
  if (!form) {
    return (
      <AppLayout>
        <div className="max-w-[1000px] mx-auto px-4 md:px-6 py-6 text-sm text-[#A0A0A0]">Loading settings…</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Settings</h1>
          <p className="text-sm text-[#A0A0A0]">Venue preferences, staff PINs, and WhatsApp integration via Meta Cloud API</p>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Section nav — top tabs on mobile, left rail on desktop. Ordering/Billing are hidden
              unless the venue has the ordering feature. */}
          <nav className="flex md:flex-col gap-1 md:w-48 shrink-0 overflow-x-auto md:overflow-visible border-b md:border-b-0 md:border-r border-white/5 pb-2 md:pb-0 md:pr-4">
            {SECTIONS.filter((s) => !s.feature || hasFeature(s.feature)).map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => switchSection(s.key)}
                className={`shrink-0 text-left text-sm font-semibold rounded-lg px-3 py-2 transition whitespace-nowrap border ${
                  section === s.key
                    ? "bg-[#9D00FF]/15 text-white border-[#9D00FF]/40"
                    : "text-[#A0A0A0] hover:text-white hover:bg-white/5 border-transparent"
                }`}
              >
                {s.label}
              </button>
            ))}
          </nav>

          {/* Active section */}
          <div className="flex-1 min-w-0 flex flex-col gap-6">

          {section === "venue" && (<>
        {/* Venue code card */}
        <div className="bg-gradient-to-br from-[#2D1B4E] to-[#1A1A1A] border border-[#9D00FF]/30 rounded-xl p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#A0A0A0]">Your Venue Code</div>
            <div className="text-3xl font-mono font-black text-[#9D00FF] mt-1" data-testid="venue-code">
              {user?.venue?.venue_code || "—"}
            </div>
            <p className="text-xs text-[#A0A0A0] mt-1">Staff use this + their PIN to log in</p>
          </div>
          <button onClick={copyCode} data-testid="copy-venue-code" className="bg-[#9D00FF] hover:bg-[#8C00E6] px-4 py-2.5 rounded-lg font-semibold flex items-center gap-2">
            <Copy size={16} /> Copy
          </button>
        </div>

        {/* Venue settings */}
        <form onSubmit={save} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 flex flex-col gap-5">
          <h2 className="text-xl font-bold">Venue Settings</h2>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Venue Name</label>
            <input
              data-testid="venue-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
            />
          </div>

          {hasFeature("bottle_locker") && (
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Default Peg Sizes</label>
            <div className="flex gap-2">
              {[30, 60].map((p) => (
                <button
                  type="button"
                  key={p}
                  data-testid={`settings-peg-${p}`}
                  onClick={() => togglePeg(p)}
                  className={`px-5 py-2.5 min-h-[44px] rounded-lg font-semibold border transition ${
                    form.default_peg_sizes.includes(p)
                      ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                      : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0]"
                  }`}
                >
                  {p}ml
                </button>
              ))}
            </div>
          </div>
          )}

          {hasFeature("bottle_locker") && (
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Default Tab Expiry</label>
            <div className="flex gap-2">
              {[30, 60, 90].map((d) => (
                <button
                  type="button"
                  key={d}
                  data-testid={`settings-expiry-${d}`}
                  onClick={() => setForm({ ...form, default_expiry_days: d })}
                  className={`px-5 py-2.5 min-h-[44px] rounded-lg font-semibold border transition ${
                    form.default_expiry_days === d
                      ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                      : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0]"
                  }`}
                >
                  {d} days
                </button>
              ))}
            </div>
          </div>
          )}

          <div className="bg-[#0F0F0F] border border-white/5 rounded-xl p-5">
            <h3 className="text-lg font-bold mb-1">Feedback Settings</h3>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Feedback Form URL (optional)</label>
              <input
                data-testid="feedback-url-input"
                value={form.feedback_form_url}
                onChange={(e) => setForm({ ...form, feedback_form_url: e.target.value })}
                placeholder="https://tabon.app/feedback"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
              />
            </div>
          </div>

          {hasFeature("loyalty_points") && (<>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Loyalty rate (pts per ₹100)</label>
            <input
              data-testid="loyalty-rate-input"
              type="number"
              min="0"
              value={form.loyalty_rate_per_100}
              onChange={(e) => setForm({ ...form, loyalty_rate_per_100: Number(e.target.value) || 0 })}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
            />
            <p className="text-xs text-[#A0A0A0] mt-1">Points earned on every bill settled at the billing counter — dine-in, takeaway, room and the venue's own delivery all earn this rate. A ₹1,500 bill earns <b className="text-white">{Math.floor(1500 / 100) * (form.loyalty_rate_per_100 || 0)} pts</b>.</p>
          </div>
          </>)}

          {hasFeature("loyalty_points") && (
          <div className="bg-[#0F0F0F] border border-white/5 rounded-xl p-5">
            <h3 className="text-lg font-bold mb-1">Loyalty Points Engine</h3>
            <p className="text-xs text-[#A0A0A0] mb-4">Customers earn points on every bill. 1 point = ₹1. Points can be redeemed by staff at the counter.</p>
            <div className="mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5">Redemption Value</div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm">1 point = ₹1 (standard)</div>
            </div>

            <div className="mt-5">
              <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2">Milestone Rewards</div>
              <div className="flex flex-col divide-y divide-white/5 border border-white/10 rounded-lg mb-3">
                {milestones.length === 0 && (
                  <div className="p-3 text-xs text-[#A0A0A0]">No milestones yet. Add one below.</div>
                )}
                {milestones.map((m) => (
                  <div key={m.id} data-testid={`milestone-row-${m.id}`} className="flex items-center gap-3 p-3">
                    <div className="text-[#9D00FF] font-black w-20">🏆 {m.milestone_points}</div>
                    <div className="flex-1 text-sm text-white">{m.reward_description}</div>
                    <label className="flex items-center gap-1 text-[11px] text-[#A0A0A0] cursor-pointer">
                      <input type="checkbox" checked={m.is_active}
                        onChange={() => toggleMilestone(m.id, !m.is_active)}
                        className="accent-[#9D00FF]" /> Active
                    </label>
                    <button type="button" onClick={() => removeMilestone(m.id)} className="text-[#EF4444] text-xs">Delete</button>
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-5 gap-2">
                <input
                  type="number" min="1" placeholder="Pts"
                  data-testid="ms-pts"
                  value={msForm.milestone_points}
                  onChange={(e) => setMsForm({ ...msForm, milestone_points: e.target.value })}
                  className="md:col-span-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
                <input
                  placeholder="Reward description (e.g. One free dessert)"
                  data-testid="ms-desc"
                  value={msForm.reward_description}
                  onChange={(e) => setMsForm({ ...msForm, reward_description: e.target.value })}
                  className="md:col-span-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
                <button type="button" onClick={addMilestone}
                  data-testid="ms-add"
                  className="bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-bold uppercase tracking-wider rounded-lg px-4 py-2">
                  Add
                </button>
              </div>
            </div>
          </div>
          )}

          {hasFeature("loyalty_points") && (
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Feedback bonus points</label>
            <input
              data-testid="loyalty-bonus-input"
              type="number"
              min="0"
              value={form.loyalty_feedback_bonus}
              onChange={(e) => setForm({ ...form, loyalty_feedback_bonus: Number(e.target.value) || 0 })}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
            />
          </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Timezone</label>
            <input
              data-testid="timezone-input"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
            />
          </div>

          {hasFeature("loyalty_points") && (
          <div className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3.5">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0]">Send welcome message to new customers</div>
              <p className="text-[11px] text-[#A0A0A0] mt-0.5">
                Sends a WhatsApp message when a new customer is added at the billing counter
              </p>
            </div>
            <button
              type="button"
              data-testid="welcome-message-toggle"
              onClick={() => setForm({ ...form, send_welcome_message: !form.send_welcome_message })}
              className={`ml-4 shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                form.send_welcome_message
                  ? "bg-[#9D00FF] text-white"
                  : "bg-white/10 text-[#A0A0A0]"
              }`}
            >
              {form.send_welcome_message ? "On" : "Off"}
            </button>
          </div>
          )}

          <div className="flex flex-col gap-2">
            {/* The "Show tax disclaimer" toggle was removed (Part 3): disclaimers are edited on
                the Menu screen as a list (menu_disclaimers), and public_menu reads that first, so
                this toggle was inert for any venue that had set disclaimers there. menu_pure_veg
                stays — it is not duplicated anywhere. */}
            <div className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3.5">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0]">Pure vegetarian venue</div>
                <p className="text-[11px] text-[#A0A0A0] mt-0.5">
                  Shows a "Pure Veg" badge at the top of your public menu. Per-item veg/non-veg indicators are unaffected.
                </p>
              </div>
              <button
                type="button"
                data-testid="pure-veg-toggle"
                onClick={() => {
                  const next = !form.menu_pure_veg;
                  setForm({ ...form, menu_pure_veg: next });
                  // menu_pure_veg is the one menu-scoped field left on this screen; save it on its
                  // own toggle so the main Save stays a single PUT /settings.
                  api.put("/menu/settings", { menu_pure_veg: next }).catch((err) => toast.error(formatError(err)));
                }}
                className={`ml-4 shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                  form.menu_pure_veg
                    ? "bg-[#9D00FF] text-white"
                    : "bg-white/10 text-[#A0A0A0]"
                }`}
              >
                {form.menu_pure_veg ? "On" : "Off"}
              </button>
            </div>
          </div>

          {/* Redemption Rules */}
          {hasFeature("loyalty_points") && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
              <h3 className="font-bold text-sm uppercase tracking-wider mb-1 text-white">
                Redemption Rules (optional)
              </h3>
              <p className="text-xs text-[#A0A0A0] mb-4">
                Control how customers spend points at the counter. All default to no restriction.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                    Maximum % of bill payable by points
                  </label>
                  <input
                    type="number" min="1" max="100" step="1"
                    data-testid="redemption-max-percent"
                    value={form.redemption_max_percent}
                    onChange={(e) => setForm({ ...form, redemption_max_percent: e.target.value })}
                    onBlur={() => {
                      const n = Math.min(100, Math.max(1, Math.floor(Number(form.redemption_max_percent) || 100)));
                      setForm((f) => ({ ...f, redemption_max_percent: n }));
                      updateTerms({ redemption_max_percent: n });
                    }}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
                  />
                  <p className="text-[10px] text-[#A0A0A0] mt-1">
                    100% means no cap. At 70%, a customer can pay at most ₹70 of a ₹100 bill with points.
                  </p>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                    Minimum bill to redeem (₹)
                  </label>
                  <input
                    type="number" min="0" step="1"
                    data-testid="redemption-min-bill"
                    value={form.redemption_min_bill}
                    onChange={(e) => setForm({ ...form, redemption_min_bill: e.target.value })}
                    onBlur={() => {
                      const n = Math.max(0, Math.floor(Number(form.redemption_min_bill) || 0));
                      setForm((f) => ({ ...f, redemption_min_bill: n }));
                      updateTerms({ redemption_min_bill: n });
                    }}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
                  />
                  <p className="text-[10px] text-[#A0A0A0] mt-1">0 means no minimum.</p>
                </div>
              </div>

              <div className="mt-4">
                <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                  Same-day redemption
                </label>
                <select
                  data-testid="redemption-cooldown"
                  value={form.redemption_cooldown}
                  onChange={(e) => {
                    const v = e.target.value === "same_day" ? "same_day" : "off";
                    setForm((f) => ({ ...f, redemption_cooldown: v }));
                    updateTerms({ redemption_cooldown: v });
                  }}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
                >
                  <option value="off">Allowed</option>
                  <option value="same_day">Not allowed on the same day</option>
                </select>
                <p className="text-[10px] text-[#A0A0A0] mt-1">
                  When set, points earned today become redeemable tomorrow.
                </p>
              </div>

              <p className="text-[11px] text-[#A0A0A0] mt-4">
                These rules appear automatically on your T&amp;Cs page.
                {menuSlug && (
                  <>
                    {" "}
                    <a
                      href={`/terms/${menuSlug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#9D00FF] hover:underline"
                    >
                      Preview T&amp;Cs page →
                    </a>
                  </>
                )}
              </p>
            </div>
          )}

          {/* Payments — only rendered when SA has provisioned online payment. If it
              is not provisioned, offering a choice the venue cannot use is worse
              than showing nothing, so the card does not render at all. */}
          {paymentCfg.enabled && paymentCfg.provider && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
              <h3 className="font-bold text-sm uppercase tracking-wider mb-1 text-white">
                Payments
              </h3>
              <p className="text-xs text-[#A0A0A0] mb-4">
                Online payment is active for this venue. Choose how takeaway customers pay.
              </p>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                Takeaway payment
              </label>
              <select
                data-testid="payment-methods-takeaway"
                value={(() => {
                  const m = form.payment_methods_takeaway || ["cash"];
                  const hasCash = m.includes("cash"), hasOnline = m.includes("online");
                  if (hasCash && hasOnline) return "both";
                  if (hasOnline) return "online";
                  return "cash";
                })()}
                onChange={(e) => {
                  const map = { cash: ["cash"], online: ["online"], both: ["cash", "online"] };
                  const v = map[e.target.value] || ["cash"];
                  setForm((f) => ({ ...f, payment_methods_takeaway: v }));
                  updateTerms({ payment_methods_takeaway: v });
                }}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
              >
                <option value="cash">Pay at counter only</option>
                <option value="online">Pay online only</option>
                <option value="both">Customer chooses</option>
              </select>
              <p className="text-[10px] text-[#A0A0A0] mt-1">
                Pay online only means the order reaches your kitchen after the customer has paid.
              </p>
            </div>
          )}

          {/* Loyalty Programme Rules */}
          {hasFeature("loyalty_points") && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
              <h3 className="font-bold text-sm uppercase tracking-wider mb-1 text-white">
                Loyalty Programme Rules (optional)
              </h3>
              <p className="text-xs text-[#A0A0A0] mb-3">
                One rule per line. Shown to customers as a numbered list alongside TabOn's
                standard terms — minimum spend, offer restrictions, seasonal exclusions.
              </p>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-[#A0A0A0]">
                  {(form.loyalty_terms || []).length}/10 rules · max 200 characters each
                </p>
                {(form.loyalty_terms || []).length < 10 && (
                  <button
                    type="button"
                    data-testid="loyalty-terms-add"
                    onClick={() => setForm((f) => ({ ...f, loyalty_terms: [...(f.loyalty_terms || []), ""] }))}
                    className="text-xs text-[#9D00FF] hover:underline"
                  >
                    + Add rule
                  </button>
                )}
              </div>
              {(form.loyalty_terms || []).map((rule, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input
                    maxLength={200}
                    data-testid={`loyalty-terms-line-${i}`}
                    value={rule}
                    onChange={(e) => {
                      const next = [...form.loyalty_terms];
                      next[i] = e.target.value;
                      setForm({ ...form, loyalty_terms: next });
                    }}
                    onBlur={() => updateTerms({ loyalty_terms: form.loyalty_terms })}
                    placeholder={`Rule ${i + 1} — e.g. Points cannot be combined with other offers`}
                    className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
                  />
                  <button
                    type="button"
                    aria-label="Remove rule"
                    onClick={() => {
                      const next = (form.loyalty_terms || []).filter((_, j) => j !== i);
                      setForm({ ...form, loyalty_terms: next });
                      updateTerms({ loyalty_terms: next });
                    }}
                    className="text-[#A0A0A0] hover:text-red-400 shrink-0 px-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {(form.loyalty_terms || []).length === 0 && (
                <p className="text-[11px] text-[#A0A0A0]">No programme rules added.</p>
              )}
              {menuSlug && (
                <div className="flex items-center justify-end mt-2">
                  <a
                    href={`/terms/${menuSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#9D00FF] hover:underline"
                  >
                    Preview T&amp;Cs page →
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Bottle Locker Rules */}
          {hasFeature("bottle_locker") && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
              <h3 className="font-bold text-sm uppercase tracking-wider mb-1 text-white">
                Bottle Locker Rules (optional)
              </h3>
              <p className="text-xs text-[#A0A0A0] mb-3">
                Add conditions specific to your bottle locker. Shown alongside TabOn's standard
                storage terms on the T&amp;Cs page.
              </p>
              <textarea
                data-testid="bottle-locker-terms-textarea"
                value={form.bottle_locker_terms}
                onChange={(e) => setForm({ ...form, bottle_locker_terms: e.target.value.slice(0, 1500) })}
                onBlur={() => updateTerms({ bottle_locker_terms: form.bottle_locker_terms.trim() || null })}
                placeholder="e.g. Bottle locker not available on event nights. Bottles unclaimed after 60 days are forfeited."
                rows={5}
                maxLength={1500}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none focus:border-[#9D00FF] focus:outline-none"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-[10px] text-[#A0A0A0]">
                  {(form.bottle_locker_terms || "").length}/1500
                </p>
                {menuSlug && (
                  <a
                    href={`/terms/${menuSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#9D00FF] hover:underline"
                  >
                    Preview T&amp;Cs page →
                  </a>
                )}
              </div>

              {/* QR Menu bottle item label */}
              <div className="mt-5">
                <div className="text-sm font-semibold text-white mb-0.5">QR Menu — Bottle Item Label</div>
                <p className="text-xs text-[#A0A0A0] mb-3">
                  Controls what shows on bottle items on the QR menu when no custom promo text is set.
                </p>
                <div className="flex flex-col gap-2.5">
                  {[
                    { value: "none", label: "None", sub: "Don't show anything automatically" },
                    { value: "savings_calculator", label: "Savings Calculator", sub: "Show bottle vs peg savings on eligible items" },
                  ].map(({ value, label, sub }) => (
                    <label key={value} className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="bottle_menu_promo"
                        value={value}
                        checked={form.bottle_menu_promo === value}
                        onChange={() => setForm({ ...form, bottle_menu_promo: value })}
                        className="accent-[#9D00FF] mt-0.5 shrink-0"
                      />
                      <div>
                        <div className="text-sm text-white">{label}</div>
                        <div className="text-[11px] text-[#A0A0A0]">{sub}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Event Campaign Frequency Cap */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
            <h3 className="font-bold text-sm uppercase tracking-wider text-white mb-1">Event Campaign Frequency Cap</h3>
            <p className="text-xs text-[#A0A0A0] mb-3">
              0 = no limit (customers can receive every event campaign, useful for multi-message event sequences). Set to a number to skip customers who've already received that many event campaigns in the last 7 days.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-xs text-[#A0A0A0] w-40 shrink-0">Max campaigns / 7 days</label>
              <input
                type="number" min="0" max="20"
                value={eventCap}
                onChange={(e) => setEventCap(e.target.value)}
                className="w-20 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white text-center"
              />
              <button
                type="button"
                onClick={saveEventCap}
                disabled={eventCapSaving}
                className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white text-xs font-bold rounded-lg px-4 py-2 transition"
              >
                {eventCapSaving ? "Saving…" : "Save"}
              </button>
              <span className="text-xs text-[#A0A0A0]">
                {Number(eventCap) === 0 ? "No limit — every eligible customer" : `Skip after ${eventCap} in 7 days`}
              </span>
            </div>
          </div>

          {/* Birthday Campaign */}
          {hasFeature("loyalty_points") && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-sm uppercase tracking-wider text-white">Birthday Campaign</h3>
                <button
                  type="button"
                  onClick={() => setBirthdayEnabled((v) => !v)}
                  className={`shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                    birthdayEnabled ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"
                  }`}
                >
                  {birthdayEnabled ? "On" : "Off"}
                </button>
              </div>
              <p className="text-xs text-[#A0A0A0] mb-3">
                Sends a WhatsApp campaign 3 days before each customer's birthday. Multiplied points automatically apply when they visit.
              </p>
              {birthdayEnabled && (
                <div className="flex flex-col gap-3 mt-3">
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-[#A0A0A0] w-32 shrink-0">Points Multiplier</label>
                    <input
                      type="number" min="2" max="5"
                      value={birthdayMultiplier}
                      onChange={(e) => setBirthdayMultiplier(e.target.value)}
                      className="w-16 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center"
                    />
                    <span className="text-xs text-[#A0A0A0]">x points on every bill</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-[#A0A0A0] w-32 shrink-0">Offer Window</label>
                    <input
                      type="number" min="1" max="7"
                      value={birthdayWindow}
                      onChange={(e) => setBirthdayWindow(e.target.value)}
                      className="w-16 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center"
                    />
                    <span className="text-xs text-[#A0A0A0]">days after birthday</span>
                  </div>
                  <p className="text-[11px] text-amber-400/80">
                    ⚠️ Only applies to customers with a date of birth on file.
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={saveBirthdaySettings}
                className="mt-4 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-bold uppercase tracking-wider rounded-lg px-4 py-2.5"
              >
                Save Birthday Settings
              </button>
            </div>
          )}

          {/* Anniversary Campaign (AA-8) — the same machine on a second date. */}
          {hasFeature("loyalty_points") && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-sm uppercase tracking-wider text-white">Anniversary Campaign</h3>
                <button
                  type="button"
                  onClick={() => setAnniversaryEnabled((v) => !v)}
                  className={`shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                    anniversaryEnabled ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"
                  }`}
                >
                  {anniversaryEnabled ? "On" : "Off"}
                </button>
              </div>
              <p className="text-xs text-[#A0A0A0] mb-3">
                Sends a WhatsApp campaign 3 days before each customer's anniversary. Multiplied points automatically apply when they visit.
              </p>
              {anniversaryEnabled && (
                <div className="flex flex-col gap-3 mt-3">
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-[#A0A0A0] w-32 shrink-0">Points Multiplier</label>
                    <input
                      type="number" min="2" max="5"
                      value={anniversaryMultiplier}
                      onChange={(e) => setAnniversaryMultiplier(e.target.value)}
                      className="w-16 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center"
                    />
                    <span className="text-xs text-[#A0A0A0]">x points on every bill</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-[#A0A0A0] w-32 shrink-0">Offer Window</label>
                    <input
                      type="number" min="1" max="7"
                      value={anniversaryWindow}
                      onChange={(e) => setAnniversaryWindow(e.target.value)}
                      className="w-16 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center"
                    />
                    <span className="text-xs text-[#A0A0A0]">days after anniversary</span>
                  </div>
                  <p className="text-[11px] text-amber-400/80">
                    ⚠️ Only applies to customers with an anniversary on file. The points multiplier works now; the WhatsApp message is pending its Meta template (tabon_anniversary_campaign) — it won't send until that is approved.
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={saveAnniversarySettings}
                className="mt-4 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-bold uppercase tracking-wider rounded-lg px-4 py-2.5"
              >
                Save Anniversary Settings
              </button>
            </div>
          )}

          {/* Visit Streak Bonus */}
          {hasFeature("loyalty_points") && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-sm uppercase tracking-wider text-white">Visit Streak Bonus</h3>
                <button
                  type="button"
                  onClick={() => setStreakEnabled((v) => !v)}
                  className={`shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                    streakEnabled ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"
                  }`}
                >
                  {streakEnabled ? "On" : "Off"}
                </button>
              </div>
              <p className="text-xs text-[#A0A0A0] mb-3">
                Bonus points when customers visit frequently. Repeats every time they complete the streak again.
              </p>
              {streakEnabled && (
                <div className="mt-3 flex flex-col gap-4">
                  {/* Visit condition */}
                  <div className="flex flex-wrap items-center gap-2 text-sm text-[#A0A0A0]">
                    <span>After</span>
                    <input
                      type="number" min="2" max="20"
                      value={streakCount}
                      onChange={(e) => setStreakCount(e.target.value)}
                      placeholder="3"
                      className="w-16 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center"
                    />
                    <span>visits within</span>
                    <input
                      type="number" min="1" max="90"
                      value={streakDays}
                      onChange={(e) => setStreakDays(e.target.value)}
                      placeholder="14"
                      className="w-16 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center"
                    />
                    <span>days, award:</span>
                  </div>

                  {/* Bonus type selector */}
                  <div className="flex gap-2">
                    {[
                      { value: "fixed", label: "Fixed pts" },
                      { value: "multiplier", label: "Points multiplier" },
                    ].map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setStreakBonusType(value)}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                          streakBonusType === value
                            ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white"
                            : "border-white/10 text-[#A0A0A0] hover:border-white/30"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Fixed bonus input */}
                  {streakBonusType === "fixed" && (
                    <div className="flex flex-wrap items-center gap-2 text-sm text-[#A0A0A0]">
                      <input
                        type="number" min="1" max="500"
                        value={streakBonus}
                        onChange={(e) => setStreakBonus(e.target.value)}
                        placeholder="50"
                        className="w-20 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center"
                      />
                      <span>bonus points</span>
                    </div>
                  )}

                  {/* Multiplier input */}
                  {streakBonusType === "multiplier" && (
                    <div className="flex flex-wrap items-center gap-2 text-sm text-[#A0A0A0]">
                      <input
                        type="number" min="1.5" max="5" step="0.5"
                        value={streakBonusMultiplier}
                        onChange={(e) => setStreakBonusMultiplier(e.target.value)}
                        placeholder="2"
                        className="w-20 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center"
                      />
                      <span>× loyalty points on the streak-completing bill</span>
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={saveStreakSettings}
                className="mt-4 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-bold uppercase tracking-wider rounded-lg px-4 py-2.5"
              >
                Save Streak Settings
              </button>
            </div>
          )}

          {/* Signup Bonus */}
          {hasFeature("loyalty_points") && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
              <h3 className="font-bold text-sm uppercase tracking-wider text-white mb-1">Signup Bonus</h3>
              <p className="text-xs text-[#A0A0A0] mb-3">
                Points credited to every new customer when they are added (manually or via bulk import).
              </p>
              <div className="flex items-center gap-3">
                <input
                  data-testid="signup-bonus-input"
                  type="number"
                  min="0"
                  max="10000"
                  value={signupBonus}
                  onChange={(e) => setSignupBonus(e.target.value)}
                  className="w-24 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white text-center focus:border-[#9D00FF]"
                />
                <span className="text-xs text-[#A0A0A0]">pts on signup (0 = disabled)</span>
              </div>
              <button
                type="button"
                onClick={saveSignupBonus}
                className="mt-4 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-bold uppercase tracking-wider rounded-lg px-4 py-2.5"
              >
                Save Signup Bonus
              </button>
            </div>
          )}

          {/* Group Rewards */}
          {hasFeature("loyalty_points") && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-sm uppercase tracking-wider text-white">Group Rewards</h3>
              </div>
              <div className="flex items-center justify-between gap-4 mt-3">
                <div>
                  <div className="text-sm font-semibold text-white">SPLIT REWARDS WITH GROUP</div>
                  <p className="text-xs text-[#A0A0A0] mt-0.5">
                    Lets staff split loyalty points across everyone at the table. Main customer shares a link via WhatsApp — each friend who claims gets their share.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, allow_points_split: !form.allow_points_split })}
                  className={`shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                    form.allow_points_split ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"
                  }`}
                >
                  {form.allow_points_split ? "On" : "Off"}
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            data-testid="save-settings-button"
            className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3 min-h-[48px]"
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </form>
          </>)}

          {section === "staff" && (<>
        {/* Override PIN */}
        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-bold mb-1">OTP Override PIN</h2>
          <p className="text-sm text-[#A0A0A0] mb-4">
            When a customer cannot receive an OTP, staff can bypass it by entering this PIN.
            Only the venue admin should know this PIN. All overrides are logged.
            Change this PIN whenever a staff member leaves.
          </p>

          {/* Current status */}
          <div className="mb-4 flex items-center gap-2">
            {user?.venue?.has_override_pin ? (
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                ✓ Override PIN is set
              </span>
            ) : (
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40">
                ⚠ No override PIN configured — OTP bypass is disabled
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={overridePin}
              onChange={(e) => setOverridePin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder={user?.venue?.has_override_pin ? "Enter new PIN to change" : "Set a 4-digit PIN"}
              className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#9D00FF] min-h-[44px] w-48 tracking-widest"
            />
            <button
              type="button"
              disabled={overridePinSaving}
              onClick={saveOverridePin}
              className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg px-5 py-2.5 min-h-[44px] text-sm transition"
            >
              {overridePinSaving ? "Saving…" : user?.venue?.has_override_pin ? "Update PIN" : "Set PIN"}
            </button>
            {user?.venue?.has_override_pin && (
              <button
                type="button"
                disabled={overridePinSaving}
                onClick={async () => {
                  if (!window.confirm("Remove the override PIN? Staff will not be able to bypass OTPs until a new PIN is set.")) return;
                  setOverridePinSaving(true);
                  try {
                    await api.put("/settings", { override_pin: "" });
                    toast.success("Override PIN removed");
                    setOverridePin("");
                    await refresh();
                  } catch (err) {
                    toast.error(formatError(err));
                  } finally {
                    setOverridePinSaving(false);
                  }
                }}
                className="border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-50 font-semibold rounded-lg px-5 py-2.5 min-h-[44px] text-sm transition"
              >
                Remove PIN
              </button>
            )}
          </div>
          <p className="text-xs text-[#A0A0A0] mt-3">
            Keep this PIN private. Do not share it with staff. If compromised, change it immediately.
          </p>
        </div>

        {/* Staff mgmt */}
        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-bold mb-3">Staff Members</h2>
          <form onSubmit={addStaff} className="flex flex-wrap gap-2 mb-4">
            <input
              data-testid="staff-name-input"
              value={newStaff.name}
              onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
              placeholder="Staff name"
              required
              className="flex-1 min-w-[200px] bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
            />
            <input
              data-testid="staff-pin-input"
              value={newStaff.pin}
              onChange={(e) => setNewStaff({ ...newStaff, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
              placeholder="4-digit PIN"
              required
              className="w-32 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white text-center font-mono tracking-[0.3em] focus:border-[#9D00FF]"
            />
            <button
              type="submit"
              data-testid="add-staff-button"
              className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] px-4 rounded-lg font-semibold flex items-center gap-2 min-h-[44px]"
            >
              <Plus size={14} /> Add
            </button>
          </form>
          <div className="divide-y divide-white/5">
            {staff.length === 0 && (
              <p className="text-sm text-[#A0A0A0]">No staff yet.</p>
            )}
            {staff.map((s) => (
              <div key={s.id} data-testid={`staff-row-${s.id}`} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-semibold">{s.name}</div>
                  <div className="text-xs text-[#A0A0A0]">PIN <span className="font-mono text-[#9D00FF]">{s.pin}</span></div>
                </div>
                <button onClick={() => delStaff(s.id)} className="text-[#EF4444] text-sm flex items-center gap-1 hover:underline">
                  <Trash2 size={14} /> Remove
                </button>
              </div>
            ))}
          </div>
        </div>
          </>)}

          {section === "loyalty" && (<>
        {/* Loyalty rates, redemption rules and programme terms currently live under the Venue
            section; splitting them out is the deferred "later job" from the restructure. */}
        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 text-xs text-[#A0A0A0]">
          Loyalty rates, redemption rules and programme terms are under the <b className="text-white">Venue</b> section for now — they move here in a later update.
        </div>

        {/* ── Stamp Programs (only if stamp_cards feature enabled) ── */}
        {hasFeature("stamp_cards") && (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-1">Stamp Programs</h2>
            <p className="text-xs text-[#A0A0A0] mb-4">Digital punch-card programs — customers earn stamps at each visit and unlock rewards.</p>

            {/* Existing programs */}
            <div className="flex flex-col divide-y divide-white/5 border border-white/10 rounded-lg mb-4">
              {stampPrograms.length === 0 && (
                <div className="p-3 text-xs text-[#A0A0A0]">No stamp programs yet.</div>
              )}
              {stampPrograms.map((sp) => (
                <div key={sp.id} data-testid={`stamp-program-row-${sp.id}`} className="p-3">
                  {spEditId === sp.id ? (
                    /* Inline edit row */
                    <div className="flex flex-col gap-2">
                      <div className="grid sm:grid-cols-2 gap-2">
                        <input
                          value={spEditForm.name ?? sp.name}
                          onChange={(e) => setSpEditForm({ ...spEditForm, name: e.target.value })}
                          placeholder="Program name"
                          className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
                        />
                        <input
                          value={spEditForm.item_label ?? sp.item_label}
                          onChange={(e) => setSpEditForm({ ...spEditForm, item_label: e.target.value })}
                          placeholder="Item label (e.g. Coffee)"
                          className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
                        />
                        <input
                          type="number" min="1"
                          value={spEditForm.required_stamps ?? sp.required_stamps}
                          onChange={(e) => setSpEditForm({ ...spEditForm, required_stamps: Number(e.target.value) })}
                          placeholder="Stamps required"
                          className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
                        />
                        <input
                          value={spEditForm.reward_title ?? sp.reward_title}
                          onChange={(e) => setSpEditForm({ ...spEditForm, reward_title: e.target.value })}
                          placeholder="Reward title"
                          className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => saveStampProgram(sp.id)}
                          className="bg-[#9D00FF] text-white text-xs font-bold rounded-lg px-3 py-1.5 hover:bg-[#8C00E6]"
                        >Save</button>
                        <button
                          type="button"
                          onClick={() => setSpEditId(null)}
                          className="bg-white/5 text-[#A0A0A0] text-xs rounded-lg px-3 py-1.5"
                        >Cancel</button>
                      </div>
                    </div>
                  ) : (
                    /* Display row */
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-white">{sp.name}</div>
                        <div className="text-xs text-[#A0A0A0]">
                          {sp.required_stamps} {sp.item_label} stamps → <span className="text-[#9D00FF]">{sp.reward_title}</span>
                        </div>
                      </div>
                      <label className="flex items-center gap-1 text-[11px] text-[#A0A0A0] cursor-pointer shrink-0">
                        <input
                          type="checkbox"
                          checked={sp.active !== false}
                          onChange={() => toggleStampProgram(sp.id, !sp.active)}
                          className="accent-[#9D00FF]"
                        /> Active
                      </label>
                      <button
                        type="button"
                        onClick={() => { setSpEditId(sp.id); setSpEditForm({}); }}
                        className="text-[#A0A0A0] hover:text-white shrink-0"
                        title="Edit"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteStampProgram(sp.id)}
                        className="text-[#EF4444] text-xs shrink-0"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Create new program */}
            <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2">Add New Program</div>
            <div className="grid sm:grid-cols-2 gap-2 mb-2">
              <input
                data-testid="sp-name"
                value={spForm.name}
                onChange={(e) => setSpForm({ ...spForm, name: e.target.value })}
                placeholder="Program name (e.g. Coffee Card)"
                className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
              />
              <input
                data-testid="sp-item-label"
                value={spForm.item_label}
                onChange={(e) => setSpForm({ ...spForm, item_label: e.target.value })}
                placeholder="Item label (e.g. Coffee)"
                className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
              />
              <input
                data-testid="sp-stamps"
                type="number" min="1"
                value={spForm.required_stamps}
                onChange={(e) => setSpForm({ ...spForm, required_stamps: Number(e.target.value) || 10 })}
                placeholder="Stamps required"
                className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
              />
              <input
                data-testid="sp-reward"
                value={spForm.reward_title}
                onChange={(e) => setSpForm({ ...spForm, reward_title: e.target.value })}
                placeholder="Reward title (e.g. 1 Free Coffee)"
                className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
              />
            </div>
            <button
              type="button"
              data-testid="sp-add"
              onClick={addStampProgram}
              className="bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-bold uppercase tracking-wider rounded-lg px-4 py-2.5 flex items-center gap-2"
            >
              <Plus size={14} /> Create Program
            </button>
          </div>
        )}
          </>)}

          {section === "ordering" && hasFeature("ordering") && (<>
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-bold mb-1">Ordering</h2>
              <p className="text-xs text-[#A0A0A0] mb-5">How customers place orders, plus dine-in table defaults. One Save for this whole section.</p>
              <div className="flex flex-col gap-5">

                <div className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3.5">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0]">Accept orders</div>
                    <p className="text-[11px] text-[#A0A0A0] mt-0.5">Pause this to stop receiving new orders without disabling the feature.</p>
                  </div>
                  <button type="button" onClick={() => { setOrderingOpen(o => !o); setOrderingDirty(true); }}
                    className={`ml-4 shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${orderingOpen ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>
                    {orderingOpen ? "Open" : "Paused"}
                  </button>
                </div>

                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2">Order type</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ value: "takeaway", label: "Takeaway" }, { value: "dine_in", label: "Dine-in" }, { value: "both", label: "Both" }].map(({ value, label }) => (
                      <button key={value} type="button" onClick={() => { setOrderingMode(value); setOrderingDirty(true); }}
                        className={`py-2.5 rounded-lg text-sm font-semibold border transition ${orderingMode === value ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0] hover:border-white/30"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {(orderingMode === "dine_in" || orderingMode === "both") && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5">Number of tables</div>
                    <input type="number" min={1} max={99} value={orderingTableCount}
                      onChange={e => { setOrderingTableCount(Number(e.target.value)); setOrderingDirty(true); }}
                      className="w-28 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#9D00FF] focus:outline-none" />
                    <p className="text-[11px] text-[#A0A0A0] mt-1">Customers select from Table 1 to {orderingTableCount}.</p>
                  </div>
                )}

                {(orderingMode === "dine_in" || orderingMode === "both") && (
                  <div className="border-t border-white/5 pt-5 grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1.5">Default covers</label>
                      <input type="number" min={1} max={20} value={defaultCovers}
                        onChange={e => { setDefaultCovers(e.target.value); setOrderingDirty(true); }}
                        className="w-28 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                      <p className="text-[11px] text-[#A0A0A0] mt-1">Pre-fills covers when a table opens (1–20).</p>
                    </div>
                    <div className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3.5 self-start">
                      <div>
                        <div className="text-sm font-semibold text-white">Require steward</div>
                        <p className="text-[11px] text-[#A0A0A0] mt-0.5">A table must have a steward assigned.</p>
                      </div>
                      <button type="button" onClick={() => { setRequireSteward(v => !v); setOrderingDirty(true); }}
                        className={`ml-4 shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${requireSteward ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>
                        {requireSteward ? "On" : "Off"}
                      </button>
                    </div>
                    {/* QR dine-in behaviour (AA-5) — a real behavioural fork that had no switch. */}
                    <div className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3.5 self-start">
                      <div>
                        <div className="text-sm font-semibold text-white">QR orders join the table bill</div>
                        <p className="text-[11px] text-[#A0A0A0] mt-0.5">A guest's QR dine-in order adds to the table's running tab. Off: each QR order stands alone.</p>
                      </div>
                      <button type="button" onClick={() => { setDineInQrAttaches(v => !v); setOrderingDirty(true); }}
                        className={`ml-4 shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${dineInQrAttaches ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>
                        {dineInQrAttaches ? "On" : "Off"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3.5">
                  <div>
                    <div className="text-sm font-semibold text-white">Auto-print KOT</div>
                    <p className="text-[11px] text-[#A0A0A0] mt-0.5">Configuration only for now — nothing prints yet.</p>
                  </div>
                  <button type="button" onClick={() => { setAutoKotEnabled(v => !v); setOrderingDirty(true); }}
                    className={`ml-4 shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${autoKotEnabled ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>
                    {autoKotEnabled ? "On" : "Off"}
                  </button>
                </div>

                <div className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3.5">
                  <div>
                    <div className="text-sm font-semibold text-white">Ask for vehicle number (takeaway)</div>
                    <p className="text-[11px] text-[#A0A0A0] mt-0.5">Shows an optional "Vehicle number" field on takeaway orders — handy for curb-side handover.</p>
                  </div>
                  <button type="button" onClick={() => { setTakeawayVehicleEnabled(v => !v); setOrderingDirty(true); }}
                    className={`ml-4 shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${takeawayVehicleEnabled ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>
                    {takeawayVehicleEnabled ? "On" : "Off"}
                  </button>
                </div>

                <div className="border-t border-white/5 pt-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1">Room Service (In-room Dining)</div>
                  <p className="text-[11px] text-[#A0A0A0] mb-3">Enable this for hotels or venues that deliver food to guest rooms via a separate QR code.</p>
                  <div className="flex items-center gap-4 mb-3">
                    <button type="button" onClick={() => { setRoomServiceEnabled(e => !e); setOrderingDirty(true); }}
                      className={`min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${roomServiceEnabled ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>
                      {roomServiceEnabled ? "On" : "Off"}
                    </button>
                    <span className="text-sm text-[#A0A0A0]">{roomServiceEnabled ? "Room service enabled" : "Room service disabled"}</span>
                  </div>
                  {roomServiceEnabled && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-[#A0A0A0] block mb-1.5">Available from (IST)</label>
                        <input type="time" value={roomServiceStart} onChange={e => { setRoomServiceStart(e.target.value); setOrderingDirty(true); }}
                          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0A0] block mb-1.5">Until (IST)</label>
                        <input type="time" value={roomServiceEnd} onChange={e => { setRoomServiceEnd(e.target.value); setOrderingDirty(true); }}
                          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                      </div>
                    </div>
                  )}
                </div>

                <button type="button" onClick={saveOrdering} disabled={orderingSettingsSaving}
                  className="self-start bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white text-sm font-bold rounded-lg px-6 py-2.5 transition">
                  {orderingSettingsSaving ? "Saving…" : "Save Ordering Settings"}
                </button>
              </div>
            </div>

            {/* Service Points — real tables/areas/channels. Mutations save immediately (like the
                Staff list), so this is separate from the Ordering section's single Save above. */}
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-bold mb-1">Service Points</h2>
              <p className="text-xs text-[#A0A0A0] mb-4">Your real tables, areas and channels — these now drive the table picker (the old table-count setting is retired). Changes here save immediately.</p>
              <ServicePointsManager />
            </div>

            {/* Printing — read-only support view (AH-3). Plain by design, and deliberately NOT on the
                dashboard. It can't confirm paper came out; it shows what was attempted and infers,
                from how long each print took, whether silent kiosk printing looks active. */}
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-xl font-bold">Printing</h2>
                <button type="button" onClick={loadPrintLog} disabled={printLogLoading}
                  className="text-xs text-[#A0A0A0] hover:text-white border border-white/10 rounded-lg px-3 py-1.5 transition disabled:opacity-50">
                  {printLogLoading ? "…" : "Refresh"}
                </button>
              </div>
              <p className="text-xs text-[#A0A0A0] mb-4">The last 50 prints from this venue's counter, and whether silent kiosk printing looks active. A support view — it cannot confirm paper actually came out.</p>
              {printVerdict && (
                <div className={`rounded-lg px-4 py-3 mb-4 text-sm font-semibold border ${
                  printVerdict.status === "active" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                    : printVerdict.status === "dialog" ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                    : "bg-white/5 border-white/10 text-[#A0A0A0]"}`}>
                  {printVerdict.label}
                  {printVerdict.sample_size > 0 && (
                    <span className="block text-[11px] font-normal text-[#A0A0A0] mt-1">
                      Last {printVerdict.sample_size}: {printVerdict.silent} silent · {printVerdict.dialog} dialog · {printVerdict.unknown} unknown
                    </span>
                  )}
                </div>
              )}
              {printLog && printLog.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[#A0A0A0]">
                        <th className="py-1 pr-3 font-medium">When</th>
                        <th className="py-1 pr-3 font-medium">Document</th>
                        <th className="py-1 pr-3 font-medium">Mode</th>
                        <th className="py-1 pr-3 font-medium">Outcome</th>
                        <th className="py-1 pr-3 font-medium">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printLog.map((r, i) => (
                        <tr key={r.id || i} className="border-t border-white/5 text-[#CFCFCF]">
                          <td className="py-1 pr-3 whitespace-nowrap">
                            {r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }) : "—"}
                          </td>
                          <td className="py-1 pr-3">{r.document === "kot" ? "KOT" : "Bill"}</td>
                          <td className={`py-1 pr-3 ${r.mode === "dialog" ? "text-amber-300" : r.mode === "silent" ? "text-emerald-300" : "text-[#A0A0A0]"}`}>
                            {r.mode}{r.duration_ms != null ? ` · ${r.duration_ms}ms` : ""}
                          </td>
                          <td className="py-1 pr-3">{r.outcome}</td>
                          <td className="py-1 pr-3">{r.user_name || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-[#555]">{printLogLoading ? "Loading…" : "No prints recorded yet."}</p>
              )}
            </div>
          </>)}

          {section === "billing" && hasFeature("ordering") && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-bold mb-1">Billing &amp; Tax</h2>
              <p className="text-xs text-[#A0A0A0] mb-5">Receipts, GST, service charge, discounts and day-close. One Save for this whole section.</p>
              <div className="flex flex-col gap-5">

                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-3">Venue details (for receipts &amp; invoices)</div>
                  <div className="flex flex-col gap-3">
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1">Full address</label>
                      <textarea value={venueAddress} onChange={e => { setVenueAddress(e.target.value.slice(0, 300)); setBillingDirty(true); }}
                        placeholder="e.g. 123, MG Road, Udaipur, Rajasthan 313001" rows={2}
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none resize-none" />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1">Contact phone</label>
                      <input type="tel" value={venuePhone} onChange={e => { setVenuePhone(e.target.value.slice(0, 20)); setBillingDirty(true); }}
                        placeholder="e.g. 9820074695"
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1">FSSAI licence number</label>
                      <input type="text" inputMode="numeric" value={fssaiNumber}
                        onChange={e => { setFssaiNumber(e.target.value.replace(/\D/g, "").slice(0, 14)); setBillingDirty(true); }}
                        placeholder="14-digit FSSAI number" maxLength={14}
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-mono focus:border-[#9D00FF] focus:outline-none" />
                      <p className="text-[11px] text-[#A0A0A0] mt-1">Required on Indian food-business bills. Prints alongside the GSTIN.</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/5 pt-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-3">Receipt type</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ value: "simple", label: "Simple Receipt", desc: "Order number, items, total, payment method" },
                      { value: "tax_invoice", label: "Tax Invoice", desc: "GSTIN, CGST/SGST breakdown, invoice number" }].map(({ value, label, desc }) => (
                      <button key={value} type="button" onClick={() => { setReceiptType(value); setBillingDirty(true); }}
                        className={`p-3 rounded-lg text-left border transition ${receiptType === value ? "bg-[#9D00FF]/15 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0] hover:border-white/30"}`}>
                        <div className="text-sm font-semibold mb-1">{label}</div>
                        <div className="text-[11px] opacity-70">{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {receiptType === "tax_invoice" && (
                  <div className="flex flex-col gap-3">
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1">GSTIN</label>
                      <input type="text" value={gstin} onChange={e => { setGstin(e.target.value.toUpperCase().slice(0, 15)); setBillingDirty(true); }}
                        placeholder="e.g. 29ABCDE1234F1Z5" maxLength={15}
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-mono focus:border-[#9D00FF] focus:outline-none" />
                      {/* A tax invoice must carry a valid GSTIN. Warn (don't block the save): the format,
                          and — when GST is actually charged — the presence. The server validates the
                          same 15-char shape and rejects a malformed value outright. */}
                      {(() => {
                        const g = (gstin || "").trim();
                        if (g && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(g))
                          return <p className="text-[11px] text-amber-400 mt-1">That doesn't look like a valid 15-character GSTIN (e.g. 29ABCDE1234F1Z5).</p>;
                        if (!g && taxTable.some(r => Number(r.rate) > 0))
                          return <p className="text-[11px] text-amber-400 mt-1">You're issuing tax invoices with GST but haven't set a GSTIN. A tax invoice must show your GSTIN to be valid.</p>;
                        return null;
                      })()}
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1">GST rate</label>
                      <p className="text-[11px] text-[#777] mb-2">Your menu's GST rate (the food group, statutory slabs). One group only until the tax invoice can report tax per rate — the billing engine supports more, but the invoice cannot yet describe them.</p>
                      <div className="flex flex-col gap-2">
                        {taxTable.map((row, i) => {
                          const isFood = row.group === "food";
                          return (
                            <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 flex flex-col gap-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                {isFood ? (
                                  <span className="px-2 py-1.5 rounded bg-white/5 text-xs font-mono text-[#A0A0A0]">food</span>
                                ) : (
                                  <input type="text" value={row.group} onChange={e => updateTaxRow(i, { group: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24) })}
                                    placeholder="group key (e.g. alcohol)" className="w-40 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-[#9D00FF] focus:outline-none" />
                                )}
                                <input type="text" value={row.label} onChange={e => updateTaxRow(i, { label: e.target.value.slice(0, 40) })}
                                  placeholder="Label (shown on the bill)" className="flex-1 min-w-[140px] bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                                {!isFood && (
                                  <button type="button" onClick={() => removeTaxGroup(i)} className="text-xs text-[#A0A0A0] hover:text-red-400 px-2 py-2 min-h-[36px]">Remove</button>
                                )}
                              </div>
                              <div className="flex items-center gap-3 flex-wrap">
                                {isFood ? (
                                  <div className="flex gap-2 flex-wrap">
                                    {[0, 5, 12, 18].map(r => (
                                      <button key={r} type="button" onClick={() => updateTaxRow(i, { rate: r })}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition ${Number(row.rate) === r ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0] hover:border-white/30"}`}>{r}%</button>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <input type="number" min={0} max={30} value={row.rate} onChange={e => updateTaxRow(i, { rate: e.target.value })}
                                      className="w-20 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                                    <span className="text-xs text-[#A0A0A0]">% (0–30)</span>
                                  </div>
                                )}
                                <div className="flex gap-1">
                                  {[["cgst_sgst", "CGST + SGST"], ["single", "Single GST"]].map(([v, l]) => (
                                    <button key={v} type="button" onClick={() => updateTaxRow(i, { split: v })}
                                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${row.split === v ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0] hover:border-white/30"}`}>{l}</button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* No "+ Add tax group" (AA-2): a second group bills correctly — compute_bill emits
                          a per-group tax_line each — but /public/invoice reports a single flat rate, so a
                          two-rate bill prints wrong and doesn't self-reconcile. The obvious second rate,
                          alcohol, is state VAT, not a GST slab `split` can express. Re-enable only once the
                          invoice reports taxable value + tax PER RATE and VAT has a model. The Remove button
                          above stays, so a venue that already has a second group can drop back to one. */}
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1">Invoice prefix</label>
                      <input type="text" value={invoicePrefix} onChange={e => { setInvoicePrefix(e.target.value.toUpperCase().slice(0, 10)); setBillingDirty(true); }}
                        placeholder="INV" maxLength={10}
                        className="w-28 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white font-mono text-sm focus:border-[#9D00FF] focus:outline-none" />
                      <p className="text-[11px] text-[#A0A0A0] mt-1">Invoice numbers appear as {invoicePrefix || "INV"}-0001, {invoicePrefix || "INV"}-0002…</p>
                    </div>
                  </div>
                )}

                <div className="border-t border-white/5 pt-5 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#A0A0A0] block mb-1.5">Discount PIN threshold (%)</label>
                    <input type="number" min={0} max={100} value={discountPinThreshold}
                      onChange={e => { setDiscountPinThreshold(e.target.value); setBillingDirty(true); }}
                      className="w-28 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                    <p className="text-[11px] text-[#A0A0A0] mt-1">A dine-in discount above this needs the manager override PIN. 0 = every discount needs it.</p>
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] block mb-1.5">Max total discount (%)</label>
                    <input type="number" min={0} max={100} value={discountMaxTotal}
                      onChange={e => { setDiscountMaxTotal(e.target.value); setBillingDirty(true); }}
                      className="w-28 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                    <p className="text-[11px] text-[#A0A0A0] mt-1">The ceiling on stacked discounts on one bill — refused beyond this even with the PIN. 100 = no cap.</p>
                  </div>
                </div>

                <div className="border-t border-white/5 pt-5">
                  <label className="text-xs text-[#A0A0A0] block mb-1.5">Receipt / thermal roll width</label>
                  <div className="flex gap-2">
                    {[80, 58].map(w => (
                      <button key={w} type="button" onClick={() => { setReceiptWidthMm(w); setBillingDirty(true); }}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${receiptWidthMm === w ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0] hover:border-white/30"}`}>
                        {w}mm
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#A0A0A0] mt-1">The paper width of your thermal printer. 80mm is standard; 58mm is the small Bluetooth size. Not yet validated against hardware.</p>
                </div>

                {/* Charges (AA-3) — the venue's full charge family: service charge, packing, delivery. */}
                <div className="border-t border-white/5 pt-5">
                  <label className="text-xs text-[#A0A0A0] block mb-1">Charges</label>
                  <p className="text-[11px] text-[#777] mb-3">Extra lines on a bill, each applied only to the order types you pick. Added only when staff choose — unless you turn on "Add automatically".</p>
                  {/* CCPA citation, scoped by its own wording to a service charge — not attached to any
                      one row, so a packing or delivery charge never prints a legal note that isn't
                      about it (AB-2). */}
                  <p className="text-[11px] text-amber-300/70 mb-3">If one of these is a <b className="text-amber-300/90">service charge</b>: {CCPA_SERVICE_CHARGE_HELP} A packing or delivery charge is a cost, not a gratuity, and isn't covered.</p>
                  <div className="flex flex-col gap-3">
                    {charges.map((c, i) => (
                      <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                          <input type="text" value={c.label} onChange={e => updateCharge(i, { label: e.target.value.slice(0, 40) })}
                            placeholder="Label (e.g. Service Charge)" className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                          <button type="button" onClick={() => removeCharge(i)} className="text-xs text-[#A0A0A0] hover:text-red-400 px-2 py-2 min-h-[36px]">Remove</button>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex gap-1">
                            {[["percent", "%"], ["fixed", "₹"]].map(([v, l]) => (
                              <button key={v} type="button" onClick={() => updateCharge(i, { mode: v })}
                                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition ${c.mode === v ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0] hover:border-white/30"}`}>{l}</button>
                            ))}
                          </div>
                          <input type="number" min={0} max={c.mode === "percent" ? 100 : undefined} step="0.5" value={c.value}
                            onChange={e => updateCharge(i, { value: e.target.value })}
                            className="w-24 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                          <span className="text-xs text-[#A0A0A0]">{c.mode === "percent" ? "% of net" : "₹ fixed"}</span>
                        </div>
                        <div>
                          <div className="text-[11px] text-[#A0A0A0] mb-1">Applies to</div>
                          <div className="flex gap-2 flex-wrap">
                            {[{ v: "dine_in", l: "Dine-in" }, { v: "takeaway", l: "Takeaway" }, { v: "room", l: "Room" }].map(({ v, l }) => (
                              <button key={v} type="button" onClick={() => toggleChargeOrderType(i, v)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${c.applies_to.includes(v) ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0] hover:border-white/30"}`}>{l}</button>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-start gap-3">
                            <button type="button" onClick={() => updateCharge(i, { auto_apply: !c.auto_apply })}
                              className={`mt-0.5 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold ${c.auto_apply ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>{c.auto_apply ? "On" : "Off"}</button>
                            <div>
                              <div className="text-sm text-white">Add automatically</div>
                              {/* Neutral hint for ANY charge. The CCPA service-charge citation is NOT
                                  per-row (AB-2): there's no structural signal that honestly marks a row
                                  as a service charge — id is off-limits (AA-1), a rename defeats label
                                  matching, and a percent delivery/convenience fee defeats mode. So the
                                  citation lives once at the section level, scoped by its own wording. */}
                              <p className="text-[11px] text-[#A0A0A0] mt-1">Added to every applicable bill without staff choosing it.</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button type="button" onClick={() => updateCharge(i, { taxable: !c.taxable })}
                              className={`min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold ${c.taxable ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>{c.taxable ? "On" : "Off"}</button>
                            <div className="text-sm text-white">GST on this charge <span className="text-[11px] text-[#A0A0A0]">— off = outside the tax base</span></div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button type="button" onClick={() => updateCharge(i, { active: !c.active })}
                              className={`min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold ${c.active ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>{c.active ? "On" : "Off"}</button>
                            <div className="text-sm text-white">Active</div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {charges.length === 0 && <p className="text-[11px] text-[#555]">No charges yet. Add one — e.g. a 10% dine-in service charge.</p>}
                  </div>
                  <button type="button" onClick={addCharge} className="mt-2 text-sm font-semibold text-[#9D00FF] hover:text-[#B94DFF]">+ Add charge</button>
                </div>

                {/* Computed bill order — shows the venue exactly what will happen (see billOrderRows). */}
                <div className="bg-[#0F0F0F] border border-white/5 rounded-xl p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2">How a bill is computed</div>
                  <div className="font-mono text-xs text-[#C0C0C0] flex flex-col gap-0.5">
                    {billOrderRows(charges).map((row, i) => (
                      <div key={i} className={`flex items-center gap-2 ${row.bold ? "text-white font-bold border-t border-white/10 pt-1 mt-1" : ""}`}>
                        <span className="w-3 text-[#9D00FF]">{row.sign}</span>
                        <span>{row.label}</span>
                        {row.note && <span className="text-[10px] text-[#A0A0A0]">← {row.note}</span>}
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#A0A0A0] mt-2">Discount-before-tax and GST-on-the-discounted-amount are verified against a real tax invoice. A charge feeds the tax base only when its "GST on this charge" is on; otherwise it sits outside GST. Charges are added per bill unless "Add automatically" is on.</p>
                </div>

                <div className="border-t border-white/5 pt-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1">Daily Summary Report</div>
                  <p className="text-[11px] text-[#A0A0A0] mb-3">A WhatsApp summary of the day's orders, sent to the venue owner's number.</p>
                  <div className="flex items-center gap-4 mb-3">
                    <button type="button" onClick={() => { setEodReportEnabled(e => !e); setBillingDirty(true); }}
                      className={`min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${eodReportEnabled ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>
                      {eodReportEnabled ? "On" : "Off"}
                    </button>
                    <span className="text-sm text-[#A0A0A0]">{eodReportEnabled ? "Report enabled" : "Report disabled"}</span>
                  </div>
                  {eodReportEnabled && (
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1.5">Send at (IST)</label>
                      <input type="time" value={eodReportTime} onChange={e => { setEodReportTime(e.target.value); setBillingDirty(true); }}
                        className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                    </div>
                  )}
                </div>

                <div className="border-t border-white/5 pt-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">Reports &amp; Day Close</div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1.5">Business day starts at (IST)</label>
                      <input type="time" value={businessDayStart} onChange={e => { setBusinessDayStart(e.target.value); setBillingDirty(true); }}
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                      <p className="text-[11px] text-[#A0A0A0] mt-1">Leave at 00:00 unless you trade past midnight.</p>
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0A0] block mb-1.5">Default opening float (₹)</label>
                      <input type="number" min="0" value={openingFloatDefault} onChange={e => { setOpeningFloatDefault(e.target.value); setBillingDirty(true); }}
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
                      <p className="text-[11px] text-[#A0A0A0] mt-1">Pre-fills the cash float when closing the day.</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3 mt-4">
                    <div>
                      <div className="text-sm font-semibold text-white">Enable Close Day</div>
                      <p className="text-[11px] text-[#A0A0A0] mt-0.5">Adds an end-of-day cash count and variance check.</p>
                    </div>
                    <button type="button" onClick={() => { setDayCloseEnabled(v => !v); setBillingDirty(true); }}
                      className={`ml-4 shrink-0 min-w-[54px] min-h-[28px] rounded-full px-3 py-1 text-xs font-bold transition-colors ${dayCloseEnabled ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"}`}>
                      {dayCloseEnabled ? "On" : "Off"}
                    </button>
                  </div>
                </div>

                <button type="button" onClick={saveBilling} disabled={billingSaving}
                  className="self-start bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white text-sm font-bold rounded-lg px-6 py-2.5 transition">
                  {billingSaving ? "Saving…" : "Save Billing Settings"}
                </button>
              </div>
            </div>
          )}

          {section === "notifications" && (<>
        {/* ── Automated WhatsApp Messages ── */}
        {templates.length > 0 && (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare size={18} className="text-[#9D00FF]" />
              <h2 className="text-xl font-bold">Automated WhatsApp Messages</h2>
            </div>
            <p className="text-xs text-[#A0A0A0] mb-4">These messages are sent automatically to your customers based on their activity. No manual action required.</p>
            <div className="flex flex-col gap-3">
              {templates.map((t) => (
                <div
                  key={t.name}
                  data-testid={`template-card-${t.name}`}
                  className="bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3.5 flex items-start gap-3"
                >
                  <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-white">{t.label}</div>
                    <p className="text-xs text-[#A0A0A0] mt-0.5">{t.trigger}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {templates.length === 0 && (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 text-xs text-[#A0A0A0]">No automated messages configured for this venue yet.</div>
        )}
          </>)}

          {section === "bottle_locker" && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 text-xs text-[#A0A0A0]">
              Bottle-locker terms are under the <b className="text-white">Venue</b> section for now — a dedicated Bottle Locker section is a later job.
            </div>
          )}

          {section === "menu" && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 text-xs text-[#A0A0A0]">
              Menu content and disclaimers are edited on the <b className="text-white">Menu</b> screen; the Pure-Veg toggle is under <b className="text-white">Venue</b>. A dedicated Menu &amp; Branding section moves here later.
            </div>
          )}

          {section === "integrations" && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 text-xs text-[#A0A0A0]">
              {user?.venue?.pos_integration
                ? <>POS integration is active. Managed by your account team.</>
                : <>No POS integration connected. Managed by your account team.</>}
            </div>
          )}

          {/* Build footer — tells QA at a glance which bundle is loaded (a stale one cost an hour). */}
          <div className="text-[11px] text-[#555] pt-2 mt-2 border-t border-white/5">Build {BUILD_ID}</div>

          </div>{/* /active section */}
        </div>{/* /nav + content */}
      </div>
    </AppLayout>
  );
}
